import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SeoHead from "./SiteSeo";
import SiteLayout from "./SiteLayout";
import { CalendlyLink, SmartLink } from "./SiteHeader";
import { Intro, Faq, HowItWorks, FinalCta } from "./sections";
import { MarketSnapshot } from "./MunicipalPage";
import ComingSoonPage from "./ComingSoonPage";
import UnavailablePage from "./UnavailablePage";
import { getPublic } from "./publicFetch";
import CityRatesV2 from "./CityRatesV2";
import { ALBERTA_CITIES } from "./nav";

const staticName = (slug) => ALBERTA_CITIES.find(([, h]) => h === `/${slug}/`)?.[0] || slug.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
import { PHONE, EMAIL } from "./nav";

const API = process.env.REACT_APP_BACKEND_URL;
const TERM_LINKS = [["5-yr-fixed", "5-Year Fixed"], ["3-yr-fixed", "3-Year Fixed"], ["5-yr-variable", "5-Year Variable"], ["2-yr-fixed", "2-Year Fixed"], ["1-yr-fixed", "1-Year Fixed"]];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" }) : null);

// City rates page — /<city>/mortgage-rates/. Cities with ported local content (market overview + buyer scenarios) render
// the Rates-design template (CityRatesV2); the rest keep the original layout until rollout.
export default function CityRatesPage() {
  const slug = useLocation().pathname.split("/").filter(Boolean)[0];
  const [data, setData] = useState(undefined);
  useEffect(() => {
    let live = true;
    setData(undefined);
    getPublic(`/city-rates/${slug}`).then((r) => live && setData(r.data || (r.error ? "error" : null)));
    return () => { live = false; };
  }, [slug]);
  if (data === undefined) return <SiteLayout loading><section className="hero"><div className="wrap"><div className="eyebrow">Loading…</div></div></section></SiteLayout>;
  if (data === null) return <ComingSoonPage />;
  if (data === "error") return <UnavailablePage title={`${staticName(slug)} Mortgage Rates | Current ${staticName(slug)} Mortgage Rates | Hello Mortgage`} />;
  if (data.has_local_content) return <CityRatesV2 data={data} />;
  return <CityRatesV1 slug={slug} />;
}

function CityRatesV1({ slug }) {
  const [s, setS] = useState({ city: undefined, rates: null, snap: null });
  useEffect(() => {
    let live = true;
    Promise.all([fetch(`${API}/api/public/municipalities/${slug}`), fetch(`${API}/api/public/featured-rates`), fetch(`${API}/api/public/market-snapshot/${slug}`)])
      .then(async ([c, r, m]) => live && setS({ city: c.ok ? await c.json() : null, rates: r.ok ? await r.json() : null, snap: m.ok ? await m.json() : null }))
      .catch(() => live && setS({ city: null, rates: null, snap: null }));
    return () => { live = false; };
  }, [slug]);
  const { city, rates, snap } = s;
  if (city === undefined) return <SiteLayout loading><section className="hero"><div className="wrap"><div className="eyebrow">Loading…</div></div></section></SiteLayout>;
  if (city === null) return <ComingSoonPage />;

  const C = city.name;
  const cards = rates?.cards || [];
  const asOf = fmtDate(rates?.updated_at_iso || rates?.last_sync_iso);
  const faqs = (city.faqs || []).map((f) => [f.q, f.a]);
  const ld = [
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.hellomortgage.ca/" },
      { "@type": "ListItem", position: 2, name: C, item: `https://www.hellomortgage.ca/${slug}/` },
      { "@type": "ListItem", position: 3, name: `${C} Mortgage Rates`, item: `https://www.hellomortgage.ca/${slug}/mortgage-rates/` }] },
    { "@context": "https://schema.org", "@type": "FinancialService", name: "Hello Mortgage Team", url: `https://www.hellomortgage.ca/${slug}/mortgage-rates/`, telephone: `+1-${PHONE}`, email: EMAIL,
      areaServed: { "@type": "City", name: C }, description: `Current ${C} mortgage rates compared across 50+ Canadian lenders, updated daily by the Hello Mortgage Team.` },
    ...(faqs.length ? [{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) }] : []),
  ];

  return (
    <SiteLayout>
      <SeoHead title={`${C} Mortgage Rates | Current ${C} Mortgage Rates | Hello Mortgage`} description={`Compare today’s best ${C} mortgage rates across 50+ banks and lenders — fixed, variable, insured and conventional — updated daily. Rates are the start; strategy is the win.`} path={`/${slug}/mortgage-rates/`} noindex />
      <Helmet><script type="application/ld+json">{JSON.stringify(ld)}</script></Helmet>
      <nav className="wrap crumbs" aria-label="Breadcrumb" data-testid="city-rates-breadcrumb"><SmartLink href="/">Home</SmartLink><span>›</span><SmartLink href={`/${slug}/`}>{C}</SmartLink><span>›</span><span aria-current="page">Mortgage rates</span></nav>
      <section className="hero rates-hero" data-testid={`city-rates-${slug}`}>
        <div className="wrap">
          <div className="eyebrow">{C} mortgage rates · updated {asOf || "daily"}</div>
          <h1 style={{ fontSize: "clamp(2.8rem,5vw,4.6rem)" }}>Today’s best {C} mortgage rates.</h1>
          <p>Compared across {rates?.lender_count || "50+"} banks and lenders, refreshed every morning. The number gets you started. The strategy is what you actually sign.</p>
          <div className="rate-cards" data-testid="city-rate-cards">
            {cards.map((c) => (
              <div className="rate-card" key={c.term} data-testid={`rate-card-${c.term.replace(/\W+/g, "-")}`}>
                <span className="rate-label">{c.label}</span>
                <strong>{c.available && c.rate ? `${Number(c.rate).toFixed(2)}%` : "Ask us"}</strong>
                <small>{c.available && c.rate ? c.channel_label : "Today’s best rate on request"}</small>
              </div>
            ))}
          </div>
          <div className="actions"><CalendlyLink className="btn" testId="city-rates-cta">Let's Get This Going</CalendlyLink><SmartLink className="textlink" href="/compare-all">Compare all lenders</SmartLink></div>
          <div className="micro">Rates shown are the best broker-channel rates available today for well-qualified borrowers; your rate depends on your file. No rate buy-down promises — just real options.</div>
        </div>
      </section>

      <section className="rates-terms"><div className="wrap">
        <Intro eyebrow="By term" heading={`Pick the ${C} rate that fits your plan.`} text="Each term has a personality. Shorter terms bet on rates falling; longer terms buy certainty. Open any term for the full lender list and the fine print that goes with it." />
        <div className="mini-grid" style={{ marginTop: 34 }}>
          {TERM_LINKS.map(([t, l]) => <SmartLink className="mini" href={`/rates/${t}`} key={t} data-testid={`term-link-${t}`}>{l}<small>All lenders · today</small></SmartLink>)}
        </div>
      </div></section>

      {city.blurb && (
        <section className="rates-local"><div className="wrap rates-local-grid">
          <div><div className="eyebrow">Mortgages in {C}</div><h2 className="display">What the {C} market means for your rate.</h2><p data-testid="city-rates-blurb">{city.blurb}</p></div>
          <div className="comparison"><h3>Rate is one line on the offer. We read the whole page.</h3><div className="compare-row"><strong>Penalties</strong><span>Fixed vs. variable break costs can dwarf a rate difference.</span></div><div className="compare-row"><strong>Prepayment</strong><span>10% or 20% a year? It changes what your mortgage can do.</span></div><div className="compare-row"><strong>Portability</strong><span>Moving within {C} shouldn’t mean paying twice.</span></div></div>
        </div></section>
      )}

      <MarketSnapshot snap={snap} cityName={C} slug={slug} />
      <HowItWorks eyebrow={`How we shop your ${C} rate`} text={`Real underwriting upfront, ${rates?.lender_count || "50+"} lenders compared, and a team that keeps everything moving.`} />
      {faqs.length > 0 && <section className="faq"><div className="wrap"><div className="intro"><div><div className="eyebrow">{C} rate questions</div><h2 className="display">The honest answers.</h2></div><Faq items={faqs} /></div></div></section>}
      <FinalCta text={`Want your actual ${C} rate, not the advertised one? Start with a free conversation. No pressure. No mortgage-speak. Just a clear plan.`} />
    </SiteLayout>
  );
}
